import {
    useState,
    useEffect,
    useCallback,
    useMemo
} from "react";

import axios from "axios";

export const useGroupHandlers = ({
    groups = [],
    sendTableDataToParent,
    setError,
    searchTerm = '',
    page,
    setPage,
    selectedGroups = [],
    setSelectedGroups,
    setApiGroups,
    rowsPerPage = 10
}) => {

    const [groupUsersMap, setGroupUsersMap] = useState({});

    const formatGroupEmployee = useCallback((user) => ({
        id: user.ssoUserId,
        employeeId: user.ssoUserId,
        employee_guid: user.ssoUserId,
        employeeName: user.fullName,
        departmentId: user.departmentId ?? null,
        departmentName: user.departmentName || "",
        groupId: user.groupId,
        groupUid: user.groupUid,
        groupName: user.groupName,
        assignmentId: 4,
        isActive: true,
        status: "Scheduled"
    }), []);

    const syncActiveGroupEmployees = useCallback((updatedMap) => {
        const activeEmployees = Object.values(updatedMap)
            .flat()
            .filter(emp => emp.isActive !== false)
            .map(formatGroupEmployee);

        sendTableDataToParent([
            ...groups.filter(item => item.assignmentId !== 4),
            ...activeEmployees
        ]);
    }, [formatGroupEmployee, groups, sendTableDataToParent]);

    // Load groups on mount
    useEffect(() => {
        const loadGroups = async () => {
            try {
                const res = await axios.get(
                    "/coordinators/get_groups_list/?IsActive=true&SortDescending=true"
                );

                if (res.data.success) {
                    setApiGroups(
                        res.data.data.map(g => ({
                            id: g.GroupId,
                            groupUid: g.GroupGUID,
                            name: g.Name
                        }))
                    );
                }
            } catch (e) {
                setError("Unable to load groups");
            }
        };

        loadGroups();
    }, [setApiGroups, setError]);

    // Fetch users for a specific group
    const fetchUsersForGroup = useCallback(async (group, search = "") => {
        if (!group?.groupUid) return [];

        try {
            const res = await axios.get(
                `/coordinators/get_groups_user_by_plant/${group.groupUid}/`,
                {
                    params: {
                        Page: 1,
                        PageSize: 500,
                        SortDescending: true,
                        Search: search
                    }
                }
            );

            if (res.data.success) {
                return res.data.data.map(u => ({
                    ...u,
                    fullName: u.fullName,
                    groupName: group.name,
                    groupId: group.id,
                    groupUid: group.groupUid,
                    status: u.status || u.Status || "Scheduled",
                    isActive: u.isActive !== false
                }));
            }
        } catch (e) {
            console.error(e);
        }

        return [];
    }, []);

    const loadUsersForGroups = useCallback(async (groupsToLoad) => {
        if (!groupsToLoad || groupsToLoad.length === 0) return [];

        let map = {};
        let allUsers = [];

        for (const group of groupsToLoad) {

            const users = await fetchUsersForGroup(group);

            map[group.id] = users;

            allUsers.push(...users);
        }

        setGroupUsersMap(prev => ({
            ...prev,
            ...map
        }));

        return allUsers;

    }, [fetchUsersForGroup]);

    // Handle adding groups - this is where users should be loaded
    const handleAddGroups = useCallback(async () => {

        if (!selectedGroups || selectedGroups.length === 0) return;


        const users = await loadUsersForGroups(selectedGroups);


        const formattedUsers = users.map(formatGroupEmployee);


        const updated = [
            ...groups,
            ...formattedUsers
        ];


        sendTableDataToParent(updated);


        setSelectedGroups([]);


    }, [
        groups,
        selectedGroups,
        loadUsersForGroups,
        formatGroupEmployee,
        sendTableDataToParent,
        setSelectedGroups
    ]);

    // Get employees based on search or loaded groups
    const employees = useMemo(() => {
        const loadedEmployees = Object.values(groupUsersMap).flat();
        const normalizedSearch = (searchTerm || "").trim().toLowerCase();

        if (!normalizedSearch) {
            return loadedEmployees;
        }

        return loadedEmployees.filter((emp) => {
            const visibleStatus = emp.isActive === false ? "Deleted" : emp.status || "Scheduled";

            return [
                emp.fullName,
                emp.employeeName,
                emp.ssoUserId,
                emp.employeeId,
                emp.groupName,
                emp.status,
                visibleStatus,
                emp.isActive === false ? "Inactive" : "Active",
            ].some((value) =>
                String(value || "").toLowerCase().includes(normalizedSearch)
            );
        });
    }, [searchTerm, groupUsersMap]);

    const totalPages = Math.ceil(employees.length / rowsPerPage) || 1;
    const currentRows = employees.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const handleDelete = useCallback((id) => {
        setGroupUsersMap(prev => {
            const updated = {};

            Object.keys(prev).forEach(groupId => {
                updated[groupId] = prev[groupId].map(emp =>
                    emp.ssoUserId === id || emp.id === id
                        ? { ...emp, isActive: false }
                        : emp
                );
            });

            syncActiveGroupEmployees(updated);

            return updated;
        });
    }, [syncActiveGroupEmployees]);

    const handleUndo = useCallback((id) => {
        setGroupUsersMap(prev => {
            const updated = {};

            Object.keys(prev).forEach(groupId => {
                updated[groupId] = prev[groupId].map(emp =>
                    emp.ssoUserId === id || emp.id === id
                        ? { ...emp, isActive: true }
                        : emp
                );
            });

            syncActiveGroupEmployees(updated);

            return updated;
        });
    }, [syncActiveGroupEmployees]);

    return {
        handleAddGroups,
        handleDelete,
        handleUndo,
        currentRows,
        totalPages,
        handlePageChange: (p) => setPage(p),
        renderPageNumbers: () =>
            Array.from({ length: totalPages }, (_, i) => i + 1)
    };
};
